import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../../components/base/SeoHead';
import { supabase } from '../../lib/supabase';
import { useSiteSettings } from '../../hooks/useSiteSettings';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  badge?: string;
}

interface Course {
  id: number;
  name: string;
  duration: string;
  price: number;
  image: string;
  description: string;
  tags: string[];
  features: Array<{ title: string; desc: string }>;
}

interface BlogPost {
  id: number;
  title: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
}

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [selectedCourseIndex, setSelectedCourseIndex] = useState(0);
  const [homeCourses, setHomeCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  const { settings: socialSettings } = useSiteSettings([
    'social_line',
    'social_facebook',
    'social_instagram',
    'social_tiktok',
    'social_youtube',
    'instagram_embed_reel',
    'instagram_embed_profile',
  ]);

  const featuredProducts: Product[] = [
    {
      id: 1,
      name: 'MOFU 智能寵物空氣清淨機',
      price: 9900,
      image: 'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/7d4217cd548672bb2ebe9dfb77a9d576.jpeg',
      category: '空氣清淨機',
      badge: '熱銷'
    }
  ];

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: '如何為寵物選擇安全的香氛產品',
      category: '寵物照顧',
      date: '2024-01-15',
      readTime: '5分鐘',
      image: 'https://public.readdy.ai/ai/img_res/0a4a53ab6a258e790edb1bb2fa581faa.jpg'
    },
    {
      id: 2,
      title: '香氛蠟燭製作入門指南',
      category: '香氛知識',
      date: '2024-01-12',
      readTime: '8分鐘',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20handmade%20scented%20candles%20arrangement%20with%20natural%20ingredients%20on%20clean%20white%20surface%2C%20candle%20making%20tutorial%20scene%2C%20soft%20warm%20lighting%2C%20minimalist%20aesthetic%2C%20aromatherapy%20crafting%20guide%20visual&width=600&height=400&seq=blog2&orientation=landscape'
    },
    {
      id: 3,
      title: '寵物空氣清淨機的選購要點',
      category: '產品指南',
      date: '2024-01-10',
      readTime: '6分鐘',
      image: 'https://readdy.ai/api/search-image?query=modern%20white%20air%20purifier%20in%20clean%20bright%20room%20with%20happy%20pets%2C%20pet-friendly%20home%20appliance%2C%20minimalist%20interior%20design%2C%20soft%20natural%20lighting%2C%20professional%20product%20lifestyle%20photography%20on%20white%20background&width=600&height=400&seq=blog3&orientation=landscape'
    }
  ];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || email.length > 500) {
      setSubmitMessage('請輸入有效的電子郵件地址');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4mm1ev7s74mkk8pe0mg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitMessage('訂閱成功！感謝您的支持');
        setEmail('');
      } else {
        setSubmitMessage('訂閱失敗，請稍後再試');
      }
    } catch (error) {
      setSubmitMessage('訂閱失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Load Instagram embed script
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    // Process Instagram embeds after script loads
    script.onload = () => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    };

    // Fetch home courses from Supabase
    fetchHomeCourses();

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchHomeCourses = async () => {
    try {
      setCoursesLoading(true);
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .eq('show_on_home', true)
        .order('home_order', { ascending: true })
        .limit(4);

      if (error) throw error;
      setHomeCourses(data || []);
    } catch (error) {
      console.error('Failed to fetch home courses:', error);
      setHomeCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#business`,
        "name": "MOFU 淨寵居",
        "description": "新北市新莊區專業寵物空氣清淨機與寵物友善香氛產品，提供手作香氛課程與香氛實驗室",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
        },
        "image": "https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png",
        "telephone": "+886-2-2202-3192",
        "email": "hello@mofu.com.tw",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "瓊林南路187巷29號",
          "addressLocality": "新北市",
          "addressRegion": "新莊區",
          "postalCode": "242",
          "addressCountry": "TW"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "25.038",
          "longitude": "121.432"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "10:00",
            "closes": "20:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday", "Sunday"],
            "opens": "11:00",
            "closes": "19:00"
          }
        ],
        "priceRange": "$$",
        "areaServed": {
          "@type": "City",
          "name": "新北市"
        },
        "sameAs": [
          "https://facebook.com/mofu",
          "https://instagram.com/tulip32240",
          "https://line.me/ti/p/@mofu"
        ]
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "MOFU 淨寵居",
        "description": "新北市新莊區寵物空氣清淨機與香氛實驗室",
        "publisher": {
          "@id": `${siteUrl}/#business`
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${siteUrl}/shop?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="新北市新莊區寵物空氣清淨機與香氛實驗室 MOFU 淨寵居 | 寶物友善香氛產品"
        description="MOFU 淨寵居位於新北市新莊區，提供專業寵物空氣清淨機、寵物友善香氛產品與手作香氛課程。結合空氣淨化技術與天然香氛藝術，為您和毛孩打造清新舒適的居家環境。寵物空氣清淨機、寵物友善香氛、香氛實驗室是我們的核心服務。"
        keywords="寵物空氣清淨機,寵物友善香氛,香氛實驗室,新北市新莊區"
        ogImage="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
        schema={schema}
      />

      {/* Hero Section */}
      <header>
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=taiwanese%20asian%20family%20relaxing%20with%20pets%20in%20modern%20minimalist%20living%20room%20with%20natural%20wood%20furniture%20and%20indoor%20plants%2C%20warm%20afternoon%20sunlight%20through%20windows%2C%20contemporary%20oriental%20interior%20design%20aesthetic%2C%20peaceful%20home%20atmosphere%20with%20dogs%20and%20cat%2C%20soft%20natural%20lighting%2C%20clean%20zen%20style%20space%20with%20traditional%20elements&width=1920&height=1080&seq=hero-taiwan-family&orientation=landscape"
              alt="Hero Background"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-transparent"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-32 w-full">
            <div className="max-w-2xl">
              <h1 className="text-4xl sm:text-6xl lg:text-8xl font-bold text-gray-800 mb-6 sm:mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                MOFU 淨寵居
              </h1>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-coral-500 mb-6 sm:mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                寶物與香氛的完美結合
              </h2>
              
              <p className="text-lg sm:text-xl text-gray-700 mb-3 sm:mb-4 leading-relaxed">
                專業寵物空氣清淨機 × 手作香氛課程
              </p>
              <p className="text-lg sm:text-xl text-gray-700 mb-8 sm:mb-12 leading-relaxed">
                為您和毛孩打造清新舒適的生活空間
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/shop"
                  className="px-8 py-4 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer text-center"
                >
                  探索產品
                </Link>
                <Link
                  to="/workshop"
                  className="px-8 py-4 bg-transparent border-2 border-coral-500 text-coral-500 rounded-full font-medium hover:bg-coral-500 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                >
                  預約課程
                </Link>
              </div>
            </div>
          </div>
        </section>
      </header>

      {/* Features Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              我們的特色服務
            </h2>
            <p className="text-gray-600 text-base sm:text-lg px-4">三大核心業務,為您提供全方位寵物生活解決方案</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* 寵物空氣清淨機 */}
            <Link to="/shop" className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-48 sm:h-64 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/e8b7c9071d86d51f58e452275a24b9c4.jpeg"
                  alt="寵物空氣清淨機"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-coral-100 rounded-2xl mx-auto mb-4 sm:mb-6">
                  <i className="ri-windy-line text-2xl sm:text-3xl text-coral-500"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">寵物空氣清淨機</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  專為寵物家庭設計的智能空氣清淨機，有效去除異味、毛髮和過敏原，讓家中空氣更清新
                </p>
                <span className="text-coral-500 font-medium inline-flex items-center hover:gap-2 transition-all whitespace-nowrap text-sm sm:text-base">
                  了解更多 <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            {/* 香氛實驗室 */}
            <Link to="/workshop" className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-48 sm:h-64 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/49771a2bbb8c74014126741065f33dbe.jpeg"
                  alt="香氛實驗室"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-coral-100 rounded-2xl mx-auto mb-4 sm:mb-6">
                  <i className="ri-flask-line text-2xl sm:text-3xl text-coral-500"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">香氛實驗室</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  專業講師指導，親手調配專屬香氛。從蠟燭到香水，探索香氛藝術的無限可能
                </p>
                <span className="text-coral-500 font-medium inline-flex items-center hover:gap-2 transition-all whitespace-nowrap text-sm sm:text-base">
                  預約課程 <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            {/* 寵物友善香氛 */}
            <Link to="/shop" className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="relative h-48 sm:h-64 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c4fc6a6ad19f9eae63aaa3433241b652.jpeg"
                  alt="寵物友善香氛"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-coral-100 rounded-2xl mx-auto mb-4 sm:mb-6">
                  <i className="ri-heart-line text-2xl sm:text-3xl text-coral-500"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">寵物友善香氛</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  特別研發的寵物安全香氛系列，溫和不刺激，讓您的家充滿怡人香氣的同時，也能確保毛孩的健康與舒適
                </p>
                <span className="text-coral-500 font-medium inline-flex items-center hover:gap-2 transition-all whitespace-nowrap text-sm sm:text-base">
                  選購商品 <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Workshop Section - Dynamic from Supabase */}
      {!coursesLoading && homeCourses.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-orange-50 via-coral-50 to-orange-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                手作香氛教室實驗室
              </h2>
              <p className="text-gray-600 text-base sm:text-lg px-4">獨一無二的寵物香氛調配體驗，在專業指導下親手為愛寵製作專屬香氛</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Left Column - Course Cards */}
              <div className="space-y-4 sm:space-y-6">
                {homeCourses.map((course, index) => (
                  <div 
                    key={course.id}
                    onClick={() => setSelectedCourseIndex(index)}
                    className={`bg-white rounded-2xl sm:rounded-3xl border-2 overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer ${
                      selectedCourseIndex === index ? 'border-orange-500 shadow-xl' : 'border-orange-200'
                    }`}
                  >
                    <div className="p-5 sm:p-8">
                      <div className="flex items-start justify-between mb-3 sm:mb-4 gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2">{course.name}</h3>
                          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                            <i className="ri-time-line"></i>
                            <span>{course.duration}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xl sm:text-3xl font-bold text-orange-500 whitespace-nowrap">NT$ {course.price.toLocaleString()}</div>
                        </div>
                      </div>
                      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{course.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {course.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="px-3 py-1.5 sm:px-4 sm:py-2 bg-orange-100 text-orange-600 rounded-full text-xs sm:text-sm font-medium">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Column - Large Image and Content */}
              <div className="space-y-4 sm:space-y-6">
                {/* Large Image */}
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden h-64 sm:h-96 group">
                  <img
                    src={homeCourses[selectedCourseIndex].image}
                    alt={homeCourses[selectedCourseIndex].name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {homeCourses[selectedCourseIndex].name}
                    </h3>
                    <p className="text-white/90 text-xs sm:text-sm">在專業講師的指導下，親手調配專屬於您和寵物的天然香氛</p>
                  </div>
                </div>

                {/* Course Content List */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 border-2 border-orange-200">
                  <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6">課程特色：</h4>
                  <ul className="space-y-3 sm:space-y-4">
                    {homeCourses[selectedCourseIndex].features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 sm:gap-3">
                        <i className="ri-checkbox-circle-fill text-orange-500 text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                        <div className="flex-1 min-w-0">
                          <strong className="text-sm sm:text-base text-gray-800">{feature.title}</strong>
                          <p className="text-gray-600 text-xs sm:text-sm mt-1">{feature.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-12 sm:mt-16 max-w-4xl mx-auto px-4">
              <Link
                to="/workshop"
                className="flex items-center justify-center gap-3 w-full px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-orange-500 to-coral-500 text-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                立即預約課程
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Loading Skeleton for Workshop Section */}
      {coursesLoading && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-orange-50 via-coral-50 to-orange-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="h-12 bg-gray-200 rounded-lg w-96 mx-auto mb-4 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded-lg w-64 mx-auto animate-pulse"></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              <div className="space-y-4 sm:space-y-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                    <div className="h-16 bg-gray-200 rounded mb-4"></div>
                    <div className="flex gap-2">
                      <div className="h-8 bg-gray-200 rounded-full w-20"></div>
                      <div className="h-8 bg-gray-200 rounded-full w-24"></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-4 sm:space-y-6">
                <div className="h-64 sm:h-96 bg-gray-200 rounded-2xl sm:rounded-3xl animate-pulse"></div>
                <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-12 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Products Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              熱門商品
            </h2>
            <p className="text-gray-600 text-base sm:text-lg px-4">精選寵物友善的香氛產品與空氣清淨設備</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Single Featured Product */}
            <Link
              to={`/product/${featuredProducts[0].id}`}
              className="group relative overflow-hidden rounded-2xl sm:rounded-3xl cursor-pointer bg-gray-50 block"
            >
              <div className="relative h-[500px] sm:h-[700px] overflow-hidden">
                <div className="w-full h-full">
                  <img
                    src={featuredProducts[0].image}
                    alt={featuredProducts[0].name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                
                {featuredProducts[0].badge && (
                  <div className="absolute top-4 sm:top-8 right-4 sm:right-8 px-4 sm:px-5 py-1.5 sm:py-2 bg-coral-500 text-white rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                    {featuredProducts[0].badge}
                  </div>
                )}
                
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12 text-white">
                  <p className="text-xs sm:text-sm mb-3 sm:mb-4 opacity-90 uppercase tracking-wider">{featuredProducts[0].category}</p>
                  <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">{featuredProducts[0].name}</h3>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">NT$ {featuredProducts[0].price.toLocaleString()}</p>
                  <div className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-coral-500 rounded-full font-medium text-sm sm:text-base lg:text-lg group-hover:bg-coral-500 group-hover:text-white transition-colors whitespace-nowrap">
                    查看詳情
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base"
            >
              查看全部商品
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              寶物與香氛知識
            </h2>
            <p className="text-gray-600 text-base sm:text-lg px-4">分享寵物照顧與香氛生活的實用資訊</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <span className="inline-block px-3 py-1 bg-coral-100 text-coral-500 rounded-full text-xs font-medium mb-3 whitespace-nowrap">
                    {post.category}
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-coral-500 transition-colors">
                    {post.title}
                  </h3>
                  <div className="flex items-center text-xs sm:text-sm text-gray-500">
                    <span>{post.date}</span>
                    <span className="mx-2">·</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/blog"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white text-coral-500 border-2 border-coral-500 rounded-full font-medium hover:bg-coral-500 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base"
            >
              查看更多文章
            </Link>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              探索香氛世界
            </h2>
            <p className="text-gray-600 text-base sm:text-lg px-4">透過 Instagram 了解更多寵物友善香氛的知識與應用</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Instagram Post 1 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative overflow-hidden bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                <iframe
                  src={socialSettings['instagram_embed_reel'] || 'https://www.instagram.com/reel/DTNJVoIkyA3/embed'}
                  className="w-full h-[500px] sm:h-[600px] border-0 rounded-xl sm:rounded-2xl"
                  scrolling="no"
                  allowTransparency={true}
                  allow="encrypted-media"
                  title="Instagram Reel"
                ></iframe>
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">香香是我是香香 I 寵物香氛美學</h3>
                <p className="text-sm sm:text-base text-gray-600">了解如何為您的毛孩選擇安全又舒適的香氛產品，打造溫馨的居家環境</p>
              </div>
            </div>

            {/* Instagram Post 2 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative overflow-hidden bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                <iframe
                  src={socialSettings['instagram_embed_profile'] || 'https://www.instagram.com/tulip32240/embed'}
                  className="w-full h-[500px] sm:h-[600px] border-0 rounded-xl sm:rounded-2xl"
                  scrolling="no"
                  allowTransparency={true}
                  allow="encrypted-media"
                  title="Instagram Post 2"
                ></iframe>
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">寵物香氛生活分享</h3>
                <p className="text-sm sm:text-base text-gray-600">跟著專業講師一步步學習手作香氛蠟燭，創造屬於自己的獨特香氛</p>
              </div>
            </div>
          </div>

          {/* Mobile View - Single Column with Better Spacing */}
          <div className="mt-6 sm:mt-8 text-center">
            <a
              href={socialSettings['social_instagram'] || 'https://www.instagram.com/'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-medium hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base"
            >
              <i className="ri-instagram-fill text-lg sm:text-xl"></i>
              在 Instagram 上追蹤我們
            </a>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-r from-coral-400 to-coral-500">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            加入我們的社群
          </h2>
          <p className="text-white/90 text-base sm:text-lg mb-8 sm:mb-12 px-4">追蹤我們，獲取最新產品資訊與香氛知識</p>
          
          <div className="flex justify-center gap-4 sm:gap-6 flex-wrap">
            <a
              href={socialSettings['social_line'] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full text-coral-500 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-line-fill text-xl sm:text-2xl"></i>
            </a>
            <a
              href={socialSettings['social_facebook'] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full text-coral-500 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-facebook-fill text-xl sm:text-2xl"></i>
            </a>
            <a
              href={socialSettings['social_instagram'] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full text-coral-500 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-instagram-fill text-xl sm:text-2xl"></i>
            </a>
            <a
              href={socialSettings['social_tiktok'] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full text-coral-500 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-tiktok-fill text-xl sm:text-2xl"></i>
            </a>
            <a
              href={socialSettings['social_youtube'] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full text-coral-500 hover:scale-110 hover:rotate-6 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-youtube-fill text-xl sm:text-2xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            訂閱電子報
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 px-4">
            第一時間獲取新品資訊、課程優惠與香氛知識
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto px-4" data-readdy-form id="newsletter-form">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="請輸入您的電子郵件"
              maxLength={500}
              required
              className="flex-1 px-4 sm:px-5 py-3 border-2 border-gray-200 rounded-full focus:outline-none focus:border-coral-500 transition-colors text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 sm:px-7 py-3 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm sm:text-base"
            >
              {isSubmitting ? '訂閱中...' : '訂閱'}
            </button>
          </form>
          
          {submitMessage && (
            <p className={`mt-3 text-sm ${submitMessage.includes('成功') ? 'text-green-600' : 'text-red-600'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
